import { Cell, Grid } from 'bold-ui'
import { Form, FormRenderProps, useListFieldStatus } from 'components/form'
import { CiapSelectField, CiapSelectFieldModel } from 'components/form/field/select/CiapSelectField/CiapSelectField'
import { useCallback } from 'react'
import { MetaArray, metaPath } from 'util/metaPath'
import { isEmpty } from 'util/validation/Util'
import { SoapEditableItem } from 'view/atendimentos/atendimento-individual/model'
import { EditableList, useEditableListField } from 'view/atendimentos/detail/components/EditableList'
import { CidadaoAtendimento } from 'view/atendimentos/types/CidadaoAtendimento'
import { getSexoCidadao } from 'view/atendimentos/utils/util-sexoCidadao'

import { MotivoConsultaRow } from './MotivoConsultaRow'

export interface MotivoConsultaFieldModel extends SoapEditableItem {
  _id: ID
  ciap: CiapSelectFieldModel
  observacao?: string
}

export interface MotivoConsultaFieldProps {
  name: MetaArray<MotivoConsultaFieldModel>
  cidadao?: CidadaoAtendimento
  controlModifiedStatus?: boolean
}

const path = metaPath<MotivoConsultaFieldModel>()

export function MotivoConsultaField(props: MotivoConsultaFieldProps) {
  const { name, cidadao, controlModifiedStatus = false } = props

  const {
    handleSubmit,
    handleRowChanged,
    removeItem,
    resetItemToInitialValue,
    input: { value },
  } = useEditableListField({
    name,
    isSameRow,
  })

  const { fieldStatus, disabledValues } = useListFieldStatus({
    name,
    controlModifiedStatus,
    isSameRow,
  })

  const ciapIsEqual = (ciap: CiapSelectFieldModel) => (value || []).some((o2) => ciap.id === o2.ciap.id)

  const itemIsDisabled = useCallback(
    (ciap: CiapSelectFieldModel) => disabledValues.some((o2) => ciap.id === o2.ciap.id),
    [disabledValues]
  )

  const renderForm = (props: FormRenderProps<MotivoConsultaFieldModel>) => {
    const handleChange = (newValue: CiapSelectFieldModel) => {
      newValue && !ciapIsEqual(newValue) && props.form.submit()
    }

    return (
      <CiapSelectField
        name={path.ciap}
        label='Motivo da consulta (CIAP 2)'
        onChange={handleChange}
        itemIsEqual={ciapIsEqual}
        sexo={getSexoCidadao(cidadao)}
        excluirCIAPsDAB={true}
        menuMinWidth={620}
        itemIsDisabled={!isEmpty(disabledValues) ? itemIsDisabled : undefined}
        dataTestId='subjetivoCiap'
      />
    )
  }

  return (
    <Grid gapVertical={0.5}>
      <Cell size={8}>
        <Form<MotivoConsultaFieldModel> render={renderForm} onSubmit={handleSubmit} {...props} />
      </Cell>

      {!controlModifiedStatus && !isEmpty(value) && (
        <Cell size={12}>
          <EditableList>
            {value.map((item) => (
              <MotivoConsultaRow key={item._id} model={item} onChange={handleRowChanged} onRemove={removeItem} />
            ))}
          </EditableList>
        </Cell>
      )}

      {controlModifiedStatus && !isEmpty(fieldStatus) && (
        <Cell size={12}>
          <EditableList>
            {Array.from(fieldStatus, ([item, status]) => (
              <MotivoConsultaRow
                key={item._id}
                model={item}
                onChange={handleRowChanged}
                onRemove={removeItem}
                onReset={resetItemToInitialValue}
                status={status}
                controlModifiedStatus
              />
            ))}
          </EditableList>
        </Cell>
      )}
    </Grid>
  )
}

const isSameRow = (itemA: MotivoConsultaFieldModel, itemB: MotivoConsultaFieldModel) => itemA.ciap.id === itemB.ciap.id
